import {
	useGetApiV1SelectionsJobInformationSuspense,
	usePutApiV1SelectionsJobInformationJobInformationId,
} from "@/api/recrutaAI/generated/aISaaSAPI";
import { RecruitPositionDetailHeader } from "@/features/recruitPosition/components/recruitPositionDetailHeader";
import {
	Accordion,
	Combobox,
	Flex,
	Group,
	Pill,
	PillsInput,
	Stack,
	TextInput,
	Title,
	useCombobox,
} from "@mantine/core";
import { Text } from "@mantine/core";
import { CheckIcon } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { Link, RichTextEditor } from "@mantine/tiptap";
import { useNavigate, useParams } from "@tanstack/react-router";
import Highlight from "@tiptap/extension-highlight";
import SubScript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useMemo, useState } from "react";

export const RecruitPositionDetailPage = () => {
	const [title, setTitle] = useState("");
	const [categories, setCategories] = useState<string[]>([]);
	const [comboboxSearchValue, setComboboxSearchValue] = useState("");
	const [comboboxCategoriesData, setComboboxCategoriesData] = useState<
		string[]
	>([]);
	const [content, setContent] = useState("");
	const [contentText, setContentText] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const combobox = useCombobox({
		onDropdownClose: () => combobox.resetSelectedOption(),
		onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
	});

	const navigate = useNavigate();

	const { recruitPositionId } = useParams({
		from: "/_authenticated/recruit-positions/$recruitPositionId/",
	});

	const { data: getApiV1SelectionsJobInformationResponse } =
		useGetApiV1SelectionsJobInformationSuspense({
			offset: 0,
			limit: 1000,
		});

	const { mutate: putApiV1SelectionsJobInformationJobInformationId } =
		usePutApiV1SelectionsJobInformationJobInformationId({
			mutation: {
				onSuccess: () => {
					setIsSubmitting(false);
					notifications.show({
						title: "選考基準を更新しました",
						message: "選考基準を更新しました。",
						color: "green",
					});
					navigate({ to: "/recruit-positions" });
				},
				onError: () => {
					setIsSubmitting(false);
					notifications.show({
						title: "選考基準の更新に失敗しました",
						message: "選考基準の更新に失敗しました。",
						color: "red",
					});
				},
			},
		});

	const selectedRecruitPosition = useMemo(
		() =>
			getApiV1SelectionsJobInformationResponse?.jobInformation.find(
				(recruitPosition) =>
					recruitPosition.jobInformationId === recruitPositionId,
			),
		[getApiV1SelectionsJobInformationResponse, recruitPositionId],
	);

	const editor = useEditor({
		extensions: [
			StarterKit,
			Underline,
			Link,
			Superscript,
			SubScript,
			Highlight,
			TextAlign.configure({ types: ["heading", "paragraph"] }),
		],
		content,
		onUpdate: ({ editor }) => {
			setContent(editor.getHTML());
			setContentText(editor.getText());
		},
	});

	useEffect(() => {
		if (!selectedRecruitPosition) {
			return;
		}

		setTitle(selectedRecruitPosition.name);
		setCategories(
			selectedRecruitPosition.category.map((category) => category.title),
		);
		setComboboxCategoriesData(
			selectedRecruitPosition.category.map((category) => category.title),
		);
		setContent(selectedRecruitPosition.body);
		setContentText(selectedRecruitPosition.body);
		if (editor) {
			editor.commands.setContent(
				selectedRecruitPosition.body.replace(/\n/g, "<br>"),
			);
		}
	}, [selectedRecruitPosition, editor]);

	const handleSubmit = () => {
		setIsSubmitting(true);
		putApiV1SelectionsJobInformationJobInformationId({
			jobInformationId: recruitPositionId,
			data: { name: title, category: categories, body: content },
		});
	};

	const exactOptionMatch = comboboxCategoriesData.some(
		(item) => item === comboboxSearchValue,
	);

	const handleComboboxSearchValueSelect = (val: string) => {
		setComboboxSearchValue("");

		if (val === "$create") {
			setComboboxCategoriesData((current) => [...current, comboboxSearchValue]);
			setCategories((current) => [...current, comboboxSearchValue]);
		} else {
			setCategories((current) =>
				current.includes(val)
					? current.filter((v) => v !== val)
					: [...current, val],
			);
		}
	};

	const handleComboboxSearchValueRemove = (val: string) =>
		setCategories((current) => current.filter((v) => v !== val));

	const renderCategories = categories.map((item) => (
		<Pill
			key={item}
			withRemoveButton
			onRemove={() => handleComboboxSearchValueRemove(item)}
		>
			{item}
		</Pill>
	));

	const categoryOptions = comboboxCategoriesData
		.filter((item) =>
			item.toLowerCase().includes(comboboxSearchValue.trim().toLowerCase()),
		)
		.map((item) => (
			<Combobox.Option
				value={item}
				key={item}
				active={categories.includes(item)}
			>
				<Group gap="sm">
					{categories.includes(item) ? <CheckIcon size={12} /> : null}
					<span>{item}</span>
				</Group>
			</Combobox.Option>
		));

	const titleValidation =
		title.length === 0
			? "選考基準名を入力してください"
			: title.length > 50
				? "選考基準名は50文字以内で入力してください"
				: null;

	const categoriesWarning =
		categories.length === 0 && selectedRecruitPosition?.status !== "failed"
			? "カテゴリ評価指標が0個のため評価指標を再生成します"
			: categories.length === 0 && selectedRecruitPosition?.status === "failed"
				? "カテゴリ評価基準の生成に失敗しました。求人情報の内容を確認して再度保存ボタンを押してください"
				: selectedRecruitPosition?.status === "failed"
					? "カテゴリ評価基準の生成に失敗しました。再度設定いただき、保存ボタンを押してください"
					: null;

	const categoriesError = categories.some((category) => category.length > 30)
		? "カテゴリ評価指標名は30文字以内で入力してください"
		: null;

	const contentTextValidation =
		contentText.length === 0
			? "求人情報を入力してください"
			: contentText.length > 5000
				? "求人情報は5000文字以内で入力してください"
				: null;

	return (
		<Stack h="100vh" bg="gray.0" gap={24}>
			<RecruitPositionDetailHeader
				initialTitle={selectedRecruitPosition?.name ?? ""}
				onSubmit={handleSubmit}
				isSubmitDisabled={
					!!titleValidation ||
					!!categoriesError ||
					!!contentTextValidation ||
					selectedRecruitPosition?.sample === true ||
					isSubmitting
				}
				isSample={selectedRecruitPosition?.sample}
			/>
			<Flex gap={24} justify="center">
				<Stack gap={24}>
					<Stack w={650} gap={24}>
						<Title order={2}>基本情報</Title>
						<Stack gap={24} bg="white" p={24} style={{ borderRadius: "8px" }}>
							<TextInput
								size="md"
								label="選考基準名"
								placeholder="選考基準名"
								defaultValue={title}
								onChange={(event) => setTitle(event.currentTarget.value)}
								error={titleValidation}
							/>
							<Stack gap={0}>
								<Text size="md" fw={500}>
									カテゴリ評価指標
								</Text>
								<Combobox
									store={combobox}
									onOptionSubmit={handleComboboxSearchValueSelect}
									withinPortal={false}
								>
									<Combobox.DropdownTarget>
										<PillsInput
											onClick={() => combobox.openDropdown()}
											error={categoriesWarning || categoriesError}
										>
											<Pill.Group>
												{renderCategories}
												<Combobox.EventsTarget>
													<PillsInput.Field
														onFocus={() => combobox.openDropdown()}
														onBlur={() => combobox.closeDropdown()}
														value={comboboxSearchValue}
														placeholder="カテゴリ評価指標を入力"
														styles={(theme) => ({
															field: {
																'&::placeholder': {
																	color: theme.colors.gray[6],
																},
															},
														})}
														onChange={(event) => {
															combobox.updateSelectedOptionIndex();
															setComboboxSearchValue(event.currentTarget.value);
														}}
														onKeyDown={(event) => {
															if (
																event.key === "Backspace" &&
																comboboxSearchValue.length === 0
															) {
																event.preventDefault();
																handleComboboxSearchValueRemove(
																	categories[categories.length - 1] ?? "",
																);
															}
														}}
													/>
												</Combobox.EventsTarget>
											</Pill.Group>
										</PillsInput>
									</Combobox.DropdownTarget>

									<Combobox.Dropdown>
										<Combobox.Options>
											{categoryOptions}

											{!exactOptionMatch &&
												comboboxSearchValue.trim().length > 0 && (
													<Combobox.Option value="$create">
														{comboboxSearchValue} を追加
													</Combobox.Option>
												)}

											{exactOptionMatch &&
												comboboxSearchValue.trim().length > 0 &&
												categoryOptions.length === 0 && (
													<Combobox.Empty>見つかりませんでした</Combobox.Empty>
												)}
										</Combobox.Options>
									</Combobox.Dropdown>
								</Combobox>
							</Stack>
						</Stack>
					</Stack>
					<Stack w={650} gap={24}>
						<Title order={2}>求人情報</Title>
						<Stack gap={24} bg="white" p={24} style={{ borderRadius: "8px" }}>
							<RichTextEditor
								editor={editor}
								bd={
									contentTextValidation ? "1px solid red" : "1px solid gray.4"
								}
							>
								<RichTextEditor.Toolbar sticky stickyOffset={60}>
									<RichTextEditor.ControlsGroup>
										<RichTextEditor.Bold />
										<RichTextEditor.Italic />
										<RichTextEditor.Underline />
										<RichTextEditor.Strikethrough />
										<RichTextEditor.ClearFormatting />
										<RichTextEditor.Highlight />
										<RichTextEditor.Code />
									</RichTextEditor.ControlsGroup>

									<RichTextEditor.ControlsGroup>
										<RichTextEditor.H1 />
										<RichTextEditor.H2 />
										<RichTextEditor.H3 />
										<RichTextEditor.H4 />
									</RichTextEditor.ControlsGroup>

									<RichTextEditor.ControlsGroup>
										<RichTextEditor.Blockquote />
										<RichTextEditor.Hr />
										<RichTextEditor.BulletList />
										<RichTextEditor.OrderedList />
										<RichTextEditor.Subscript />
										<RichTextEditor.Superscript />
									</RichTextEditor.ControlsGroup>

									<RichTextEditor.ControlsGroup>
										<RichTextEditor.Link />
										<RichTextEditor.Unlink />
									</RichTextEditor.ControlsGroup>

									<RichTextEditor.ControlsGroup>
										<RichTextEditor.AlignLeft />
										<RichTextEditor.AlignCenter />
										<RichTextEditor.AlignJustify />
										<RichTextEditor.AlignRight />
									</RichTextEditor.ControlsGroup>

									<RichTextEditor.ControlsGroup>
										<RichTextEditor.Undo />
										<RichTextEditor.Redo />
									</RichTextEditor.ControlsGroup>
								</RichTextEditor.Toolbar>

								<RichTextEditor.Content
									mih={200}
									mah={400}
									style={{ overflowY: "scroll" }}
									c={contentTextValidation ? "red" : "black"}
								/>
							</RichTextEditor>
							<Text size="sm" c="red" mt={-16}>
								{contentTextValidation}
							</Text>
						</Stack>
					</Stack>
				</Stack>
				<Stack w={600} gap={24}>
					<Title order={2}>選考基準</Title>
					<Stack
						gap={24}
						bg="white"
						mah={824}
						p={24}
						style={{ borderRadius: "8px", overflowY: "auto" }}
					>
						<Title order={4} c="gray.7">
							カテゴリ評価指標
						</Title>
						<Accordion variant="separated" defaultValue={selectedRecruitPosition?.category[0]?.title}>
							{selectedRecruitPosition?.category.map((category) => (
								<Accordion.Item key={category.title} value={category.title}>
									<Accordion.Control>{category.title}</Accordion.Control>
									<Accordion.Panel
										mah={500}
										c="gray.7"
										style={{
											overflow: "hidden",
											overflowY: "scroll",
											whiteSpace: "pre-wrap",
										}}
									>
										{category.prompt}
									</Accordion.Panel>
								</Accordion.Item>
							))}
						</Accordion>
					</Stack>
				</Stack>
			</Flex>
		</Stack>
	);
};
